import { I18nManager } from "../../services/i18n-manager";
import { Product, ProductService } from "../../services/product-service";
import { CartManager } from "../../state/cart-manager";
import { DialogManager } from "./dialog-manager";

export class ProductDialog {
  private dialogElement: HTMLElement;
  private productService: ProductService;
  private cartManager: CartManager;
  private i18nManager: I18nManager;
  private dialogManager: DialogManager;

  private currentProduct: Product | null = null;
  private currentImageIndex: number = 0;
  private selectedOptions: Record<string, string> = {};
  private quantity: number = 1;

  // DOM elements
  private mainImage: HTMLElement | null = null;
  private thumbnailsContainer: HTMLElement | null = null;
  private productName: HTMLElement | null = null;
  private productDescription: HTMLElement | null = null;
  private productPrice: HTMLElement | null = null;
  private productOptions: HTMLElement | null = null;
  private quantityInput: HTMLInputElement | null = null;
  private addToCartButton: HTMLButtonElement | null = null;
  private productSpecs: HTMLElement | null = null;
  private prevButton: HTMLButtonElement | null = null;
  private nextButton: HTMLButtonElement | null = null;

  constructor(
    productService: ProductService,
    cartManager: CartManager,
    i18nManager: I18nManager,
    dialogManager: DialogManager
  ) {
    this.dialogElement = document.getElementById(
      "product-dialog"
    ) as HTMLElement;
    this.productService = productService;
    this.cartManager = cartManager;
    this.i18nManager = i18nManager;
    this.dialogManager = dialogManager;

    if (!this.dialogElement) {
      console.error("Product dialog element not found!");
      return;
    }

    // Ensure dialog starts hidden
    this.dialogElement.setAttribute("aria-hidden", "true");

    this.initializeElements();
    this.setupEventListeners();
  }

  private initializeElements(): void {
    // Query elements from dialog element (works even inside custom elements)
    this.mainImage = this.dialogElement.querySelector("#product-main-image");
    this.thumbnailsContainer = this.dialogElement.querySelector(
      "#product-thumbnails"
    );
    this.productName = this.dialogElement.querySelector("#product-detail-name");
    this.productDescription = this.dialogElement.querySelector(
      "#product-detail-description"
    );
    this.productPrice = this.dialogElement.querySelector(
      "#product-detail-price"
    );
    this.productOptions = this.dialogElement.querySelector("#product-options");
    this.quantityInput = this.dialogElement.querySelector(
      "#product-quantity"
    ) as HTMLInputElement;
    this.addToCartButton = this.dialogElement.querySelector(
      "#product-add-to-cart"
    ) as HTMLButtonElement;
    this.productSpecs = this.dialogElement.querySelector(
      "#product-detail-specs"
    );
    this.prevButton = this.dialogElement.querySelector(
      ".gallery-prev"
    ) as HTMLButtonElement;
    this.nextButton = this.dialogElement.querySelector(
      ".gallery-next"
    ) as HTMLButtonElement;

    // Debug: Log if elements are found
    if (!this.productName) {
      console.warn("Product name element not found!");
    }
    if (!this.productDescription) {
      console.warn("Product description element not found!");
    }
    if (!this.productPrice) {
      console.warn("Product price element not found!");
    }
  }

  private setupEventListeners(): void {
    // Dialog open event
    this.dialogElement.addEventListener("dialog:open", (e: Event) => {
      const customEvent = e as CustomEvent;
      const productId = customEvent.detail?.productId;
      if (productId) {
        this.loadProduct(productId);
      } else {
        // Also check data attribute as fallback
        const dataAttr = this.dialogElement.getAttribute("data-dialog-data");
        if (dataAttr) {
          try {
            const data = JSON.parse(dataAttr);
            if (data.productId) {
              this.loadProduct(data.productId);
            }
          } catch (err) {
            console.error("Failed to parse dialog data:", err);
          }
        }
      }
    });

    // Gallery navigation
    this.prevButton?.addEventListener("click", () => this.previousImage());
    this.nextButton?.addEventListener("click", () => this.nextImage());

    // Quantity controls
    this.dialogElement
      .querySelector(".quantity-decrease")
      ?.addEventListener("click", () => {
        if (this.quantity > 1) {
          this.quantity--;
          this.updateQuantityDisplay();
        }
      });

    this.dialogElement
      .querySelector(".quantity-increase")
      ?.addEventListener("click", () => {
        if (this.quantity < 99) {
          this.quantity++;
          this.updateQuantityDisplay();
        }
      });

    this.quantityInput?.addEventListener("change", (e) => {
      const value = parseInt((e.target as HTMLInputElement).value);
      if (value >= 1 && value <= 99) {
        this.quantity = value;
      } else {
        this.updateQuantityDisplay();
      }
    });

    // Add to cart
    this.addToCartButton?.addEventListener("click", () => this.addToCart());

    // Keyboard navigation
    this.dialogElement.addEventListener("keydown", (e) => {
      if (e.key === "ArrowLeft") {
        this.previousImage();
      } else if (e.key === "ArrowRight") {
        this.nextImage();
      }
    });

    // Language change
    this.i18nManager.onLanguageChange(() => {
      if (this.currentProduct) {
        this.updateTranslations();
      }
    });
  }

  init(): void {
    // Initialize with empty state
    this.resetDialog();
  }

  private async loadProduct(productId: string): Promise<void> {
    try {
      const product = await this.productService.getProductById(productId);
      if (!product) {
        throw new Error("Product not found");
      }
      this.currentProduct = product;
      this.currentImageIndex = 0;
      this.selectedOptions = {};
      this.quantity = 1;

      // Re-initialize elements in case dialog was reset
      this.initializeElements();

      this.renderProduct();
      this.updateTranslations();
    } catch (error) {
      console.error("Error loading product:", error);
      this.dialogManager.showNotification(
        this.i18nManager.t("error.load_product") || "Failed to load product",
        "error"
      );
    }
  }

  private renderProduct(): void {
    if (!this.currentProduct) return;

    const product = this.currentProduct;
    const localizedName = this.productService.getLocalizedName(product);
    const localizedDescription =
      this.productService.getLocalizedDescription(product);
    const price = this.i18nManager.formatCurrency(product.price_sek);

    // Update basic info
    if (this.productName) {
      this.productName.textContent = localizedName;
    }
    if (this.productDescription) {
      this.productDescription.textContent = localizedDescription;
    }
    if (this.productPrice) {
      this.productPrice.textContent = price;
    }

    // Render gallery
    this.renderGallery();

    // Render options
    this.renderOptions();

    // Render specifications
    this.renderSpecifications();

    // Update quantity display
    this.updateQuantityDisplay();

    // Update add to cart button
    this.updateAddToCartButton();
  }

  private renderGallery(): void {
    if (!this.currentProduct || !this.mainImage || !this.thumbnailsContainer)
      return;

    const images = this.currentProduct.images;

    if (images.length === 0) {
      // No images, show placeholder
      this.mainImage.setAttribute(
        "src",
        this.productService.getPlaceholderImage()
      );
      this.mainImage.setAttribute(
        "alt",
        this.productService.getLocalizedName(this.currentProduct)
      );
      this.thumbnailsContainer.innerHTML = "";
      this.prevButton!.style.display = "none";
      this.nextButton!.style.display = "none";
      return;
    }

    // Show navigation buttons if multiple images
    const showNav = images.length > 1;
    this.prevButton!.style.display = showNav ? "flex" : "none";
    this.nextButton!.style.display = showNav ? "flex" : "none";

    // Update main image
    this.updateMainImage();

    // Render thumbnails
    this.thumbnailsContainer.innerHTML = images
      .map(
        (image, index) => `
      <button class="gallery-thumbnail ${
        index === this.currentImageIndex ? "active" : ""
      }" 
              data-index="${index}" 
              aria-label="View image ${index + 1}">
        <image-loader src="${this.productService.getProductImageUrl(image)}" 
                      alt="${
                        this.currentProduct
                          ? this.productService.getLocalizedName(
                              this.currentProduct
                            )
                          : ""
                      } - Image ${index + 1}">
        </image-loader>
      </button>
    `
      )
      .join("");

    // Add thumbnail click listeners
    this.thumbnailsContainer
      .querySelectorAll(".gallery-thumbnail")
      .forEach((thumb, index) => {
        thumb.addEventListener("click", () => {
          this.currentImageIndex = index;
          this.updateMainImage();
          this.updateThumbnailSelection();
        });
      });
  }

  private updateMainImage(): void {
    if (!this.currentProduct || !this.mainImage) return;

    const images = this.currentProduct.images;
    if (images.length === 0) return;

    const currentImage = images[this.currentImageIndex];
    this.mainImage.setAttribute(
      "src",
      this.productService.getProductImageUrl(currentImage)
    );
    this.mainImage.setAttribute(
      "alt",
      `${this.productService.getLocalizedName(this.currentProduct)} - Image ${
        this.currentImageIndex + 1
      }`
    );
  }

  private updateThumbnailSelection(): void {
    if (!this.thumbnailsContainer) return;

    this.thumbnailsContainer
      .querySelectorAll(".gallery-thumbnail")
      .forEach((thumb, index) => {
        thumb.classList.toggle("active", index === this.currentImageIndex);
      });
  }

  private previousImage(): void {
    if (!this.currentProduct) return;

    const images = this.currentProduct.images;
    if (images.length <= 1) return;

    this.currentImageIndex =
      this.currentImageIndex > 0
        ? this.currentImageIndex - 1
        : images.length - 1;
    this.updateMainImage();
    this.updateThumbnailSelection();
  }

  private nextImage(): void {
    if (!this.currentProduct) return;

    const images = this.currentProduct.images;
    if (images.length <= 1) return;

    this.currentImageIndex =
      this.currentImageIndex < images.length - 1
        ? this.currentImageIndex + 1
        : 0;
    this.updateMainImage();
    this.updateThumbnailSelection();
  }

  private renderOptions(): void {
    if (!this.currentProduct || !this.productOptions) return;

    const product = this.currentProduct;
    const options = product.options || {};

    if (Object.keys(options).length === 0) {
      this.productOptions.innerHTML = "";
      return;
    }

    this.productOptions.innerHTML = Object.entries(options)
      .map(([key, optionAttribute]) => {
        const optionName =
          this.productService.getLocalizedOptionName(optionAttribute);

        return `
        <div class="product-option">
          <label for="option-${key}">${optionName}:</label>
          <select id="option-${key}" name="option-${key}" class="option-select" data-option="${key}">
            ${optionAttribute.values
              .map(
                (optionValue) => `
              <option value="${
                optionValue.value
              }">${this.productService.getLocalizedOptionValue(
                  optionValue
                )}</option>
            `
              )
              .join("")}
          </select>
        </div>
      `;
      })
      .join("");

    // Add option change listeners
    this.productOptions.querySelectorAll(".option-select").forEach((select) => {
      select.addEventListener("change", (e) => {
        const target = e.target as HTMLSelectElement;
        const optionKey = target.dataset.option!;
        this.selectedOptions[optionKey] = target.value;
        this.updateAddToCartButton();
      });
    });

    // Initialize selected options with first value of each option
    Object.entries(options).forEach(([key, optionAttribute]) => {
      if (optionAttribute.values.length > 0) {
        this.selectedOptions[key] = optionAttribute.values[0].value;
      }
    });
  }

  private renderSpecifications(): void {
    if (!this.currentProduct || !this.productSpecs) return;

    const product = this.currentProduct;
    const specs = (product as any).specifications || {};
    const currentLang = this.i18nManager.getCurrentLanguage();

    // Get the specifications for the current language, fallback to English, then Swedish
    const specContent = specs[currentLang] || specs.en || specs.sv || "";

    if (!specContent) {
      this.productSpecs.innerHTML = `<p>${
        this.i18nManager.t("product.no_specifications") ||
        "No specifications available"
      }</p>`;
      return;
    }

    // Display the specifications as HTML content (admin allows HTML)
    this.productSpecs.innerHTML = specContent;
  }

  private updateQuantityDisplay(): void {
    if (this.quantityInput) {
      this.quantityInput.value = this.quantity.toString();
    }
  }

  private updateAddToCartButton(): void {
    if (!this.addToCartButton) return;

    const isInCart = this.cartManager
      .getCart()
      .items.some(
        (item) =>
          item.productId === this.currentProduct?.id &&
          JSON.stringify(item.options) === JSON.stringify(this.selectedOptions)
      );

    if (isInCart) {
      this.addToCartButton.textContent =
        this.i18nManager.t("product.in_cart") || "In Cart";
      this.addToCartButton.disabled = true;
      this.addToCartButton.classList.add("glass-button--secondary");
    } else {
      this.addToCartButton.textContent =
        this.i18nManager.t("product.add_to_cart") || "Add to Cart";
      this.addToCartButton.disabled = false;
      this.addToCartButton.classList.remove("glass-button--secondary");
    }
  }

  private addToCart(): void {
    if (!this.currentProduct) return;

    const product = this.currentProduct;
    const localizedName = this.productService.getLocalizedName(product);
    const imageUrl =
      product.images.length > 0
        ? this.productService.getProductImageUrl(product.images[0])
        : this.productService.getPlaceholderImage();

    this.cartManager.addItem(
      product.id,
      localizedName,
      product.price_sek,
      this.quantity,
      this.selectedOptions,
      imageUrl
    );

    this.dialogManager.showNotification(
      this.i18nManager.t("success.item_added_to_cart") || "Item added to cart",
      "success"
    );

    this.updateAddToCartButton();
  }

  private updateTranslations(): void {
    if (!this.currentProduct) return;

    // Update dialog header title
    const dialogTitle = this.dialogElement.querySelector(
      "#product-dialog-title"
    );
    if (dialogTitle) {
      dialogTitle.textContent =
        this.i18nManager.t("product.details") || "Product Details";
    }

    // Update quantity label
    const quantityLabel = this.dialogElement.querySelector(
      ".quantity-selector label"
    );
    if (quantityLabel) {
      quantityLabel.textContent =
        this.i18nManager.t("product.quantity") || "Quantity:";
    }

    // Update product details heading
    const detailsHeading = this.dialogElement.querySelector(
      ".product-details h3"
    );
    if (detailsHeading) {
      detailsHeading.textContent =
        this.i18nManager.t("product.details") || "Product Details";
    }

    // Re-render options and specs to update translations
    this.renderOptions();
    this.renderSpecifications();
    this.updateAddToCartButton();
  }

  private resetDialog(): void {
    this.currentProduct = null;
    this.currentImageIndex = 0;
    this.selectedOptions = {};
    this.quantity = 1;

    if (this.productName) this.productName.textContent = "";
    if (this.productDescription) this.productDescription.textContent = "";
    if (this.productPrice) this.productPrice.textContent = "";
    if (this.productOptions) this.productOptions.innerHTML = "";
    if (this.productSpecs) this.productSpecs.innerHTML = "";
    if (this.thumbnailsContainer) this.thumbnailsContainer.innerHTML = "";
    if (this.mainImage) this.mainImage.setAttribute("src", "");
    if (this.prevButton) this.prevButton.style.display = "none";
    if (this.nextButton) this.nextButton.style.display = "none";
  }
}
